import React, { useRef } from 'react';
import I18n from 'i18n-js';
import { Button, Dropdown, Space } from 'antd';
import {
  bool, func, number, oneOfType, shape, string,
} from 'prop-types';
import { isOrchard } from 'helpers/userSessionSettings';
import {
  CalendarIcon,
  CombineIcon,
  CropCycleIcon,
  FieldGroupIcon,
} from 'components/cwo-icons';
import { FilterListColor } from 'components/cwo-icon-pack';
import ToggleBtnSeasonAndFieldGroup from './ToggleBtnSeasonAndFieldGroup';
import ToggleButton from '../components/toggle-button';

const translate = (key) => I18n.t(`top_menu_right_part.${key}`);
const fieldGroupsLabel = translate(isOrchard ? 'orchard_plots' : 'field_groups');
const seasonLabel = translate('current_season');
const toggleBtnKeys = ['seasonAndFieldGroups', 'productionCycles', 'machineRegions'];
const dropdownToggleBtnKeys = ['season', 'fieldGroups', 'productionCycles', 'machineRegions'];

const ToggleBtnGroup = ({
  fieldGroupsValue,
  isMachinery,
  isTimerRunning,
  loadMenuData,
  machineRegionsValue,
  productionCycleValue,
  showDrawer,
  showWorkRecord,
  timerString,
  seasonValue,
  options,
}) => {
  const {
    isShowCompanies, isShowFieldGroups, isShowProductionCycles, isShowMachineRegions,
  } = options || {};

  const dispatcherTimerContent = (showWorkRecord && isTimerRunning) ? (
    <div className="top-menu-field-groups__dispatcher-timer">
      <small>{timerString}</small>
    </div>
  ) : null;

  const filterDropdownRef = useRef(null);

  const prepareToggleBtns = ({ key, showExpandIcon }) => ({
    season: (
      <ToggleButton
        icon={<CalendarIcon />}
        iconBackgroundColor="#F6F2FE"
        id="season-btn"
        key={key}
        label={seasonLabel}
        loadMenuData={loadMenuData}
        showDrawer={showDrawer}
        showExpandIcon={showExpandIcon}
        toggleKey="0"
        value={seasonValue}
      />
    ),
    fieldGroups: isShowFieldGroups
      ? (
        <ToggleButton
          icon={<FieldGroupIcon />}
          iconBackgroundColor="#EAF6FF"
          id="field-groups-btn"
          key={key}
          label={fieldGroupsLabel}
          loadMenuData={loadMenuData}
          showDrawer={showDrawer}
          showExpandIcon={showExpandIcon}
          toggleKey="1"
          value={fieldGroupsValue}
        />
      )
      : null,
    seasonAndFieldGroups: (
      <ToggleBtnSeasonAndFieldGroup
        fieldGroupLabel={isShowCompanies ? translate('companies') : fieldGroupsLabel}
        fieldGroupValue={fieldGroupsValue}
        key={key}
        loadMenuData={loadMenuData}
        seasonLabel={seasonLabel}
        seasonValue={seasonValue}
        showDrawer={showDrawer}
      />
    ),
    productionCycles: isShowProductionCycles
      ? (
        <ToggleButton
          icon={<CropCycleIcon />}
          iconBackgroundColor="#FFF1F3"
          id="production-cycles-btn"
          key={key}
          label={translate('production_cycles')}
          loadMenuData={loadMenuData}
          showDrawer={showDrawer}
          showExpandIcon={showExpandIcon}
          toggleKey="2"
          value={productionCycleValue}
        />
      )
      : null,
    machineRegions: isShowMachineRegions
      ? (
        <ToggleButton
          dispatcherTimerContent={dispatcherTimerContent}
          icon={<CombineIcon color="#9A6412" width="20" height="20" />}
          iconBackgroundColor="#FFF3DD"
          id="machinery-regions-btn"
          isVisible={isMachinery}
          key={key}
          label={translate('machine_regions')}
          loadMenuData={loadMenuData}
          showDrawer={showDrawer}
          showExpandIcon={showExpandIcon}
          toggleKey="3"
          value={machineRegionsValue}
        />
      )
      : null,
  });

  return (
    <>
      <Space
        className="top-menu-field-groups__toggle-btn-group"
        align="center"
        size={1}
      >
        {toggleBtnKeys.map((key) => {
          const btns = prepareToggleBtns({ key, showExpandIcon: true });

          return btns[key];
        })}
      </Space>

      <div ref={filterDropdownRef} className="top-menu-field-groups__dropdown-toggle-btn-group">
        <Dropdown
          getPopupContainer={() => filterDropdownRef.current}
          menu={{
            items: dropdownToggleBtnKeys.map((key) => {
              const btns = prepareToggleBtns({ key, showExpandIcon: false });

              return { key, label: btns[key] };
            }),
          }}
          trigger={['click']}
          placement="bottom"
        >
          <Button type="text" icon={<FilterListColor />} />
        </Dropdown>
      </div>
    </>
  );
};

ToggleBtnGroup.propTypes = {
  fieldGroupsValue: oneOfType([string, number]).isRequired,
  isMachinery: bool.isRequired,
  isTimerRunning: bool.isRequired,
  loadMenuData: func.isRequired,
  machineRegionsValue: string.isRequired,
  productionCycleValue: oneOfType([string, number]).isRequired,
  seasonValue: oneOfType([string, number]).isRequired,
  showDrawer: func.isRequired,
  showWorkRecord: bool.isRequired,
  timerString: string.isRequired,
  options: shape({
    isShowCompanies: bool,
    isShowFieldGroups: bool,
    isShowProductionCycles: bool,
    isShowMachineRegions: bool,
  }).isRequired,
};

export default ToggleBtnGroup;
